import { forwardRef, type ComponentPropsWithoutRef } from 'react';

import { cn } from '../../utils';

export type FlagCode = 'MX' | 'US' | 'CN' | 'ES' | 'GB' | 'FR' | 'DE' | 'JP' | 'BR' | 'AR';

export interface FlagIconProps extends Omit<ComponentPropsWithoutRef<'span'>, 'children'> {
  /** Country code (ISO 3166-1 alpha-2) */
  code: FlagCode;
  /** Size of the flag icon */
  size?: 'sm' | 'md' | 'lg';
}

const sizeClasses = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
};

// Flag SVG paths - using simple emoji-style representations
const flagPaths: Record<
  FlagCode,
  { colors: string[]; pattern: 'horizontal' | 'vertical' | 'custom' }
> = {
  MX: { colors: ['#006847', '#FFFFFF', '#CE1126'], pattern: 'vertical' },
  US: { colors: ['#B22234', '#FFFFFF', '#3C3B6E'], pattern: 'custom' },
  CN: { colors: ['#DE2910', '#FFDE00'], pattern: 'custom' },
  ES: { colors: ['#AA151B', '#F1BF00', '#AA151B'], pattern: 'horizontal' },
  GB: { colors: ['#012169', '#C8102E', '#FFFFFF'], pattern: 'custom' },
  FR: { colors: ['#002395', '#FFFFFF', '#ED2939'], pattern: 'vertical' },
  DE: { colors: ['#000000', '#DD0000', '#FFCE00'], pattern: 'horizontal' },
  JP: { colors: ['#FFFFFF', '#BC002D'], pattern: 'custom' },
  BR: { colors: ['#009739', '#FEDD00', '#012169'], pattern: 'custom' },
  AR: { colors: ['#74ACDF', '#FFFFFF', '#74ACDF'], pattern: 'horizontal' },
};

function FlagSVG({ code, className }: { code: FlagCode; className?: string }) {
  const flag = flagPaths[code];

  if (code === 'MX') {
    return (
      <svg viewBox="0 0 24 16" className={className} aria-hidden="true">
        <rect width="8" height="16" fill="#006847" />
        <rect x="8" width="8" height="16" fill="#FFFFFF" />
        <rect x="16" width="8" height="16" fill="#CE1126" />
        {/* Simplified eagle emblem */}
        <circle cx="12" cy="8" r="2" fill="#006847" />
      </svg>
    );
  }

  if (code === 'US') {
    return (
      <svg viewBox="0 0 24 16" className={className} aria-hidden="true">
        {/* Stripes */}
        {[0, 2, 4, 6, 8, 10, 12].map(y => (
          <rect key={y} y={y * (16 / 13)} width="24" height={16 / 13} fill="#B22234" />
        ))}
        {[1, 3, 5, 7, 9, 11].map(y => (
          <rect key={y} y={y * (16 / 13)} width="24" height={16 / 13} fill="#FFFFFF" />
        ))}
        {/* Blue canton */}
        <rect width="10" height="8.6" fill="#3C3B6E" />
        {/* Simplified stars */}
        <circle cx="5" cy="4.3" r="1.5" fill="#FFFFFF" />
      </svg>
    );
  }

  if (code === 'CN') {
    return (
      <svg viewBox="0 0 24 16" className={className} aria-hidden="true">
        <rect width="24" height="16" fill="#DE2910" />
        {/* Large star */}
        <polygon points="5,2 6,5 9,5 6.5,7 7.5,10 5,8 2.5,10 3.5,7 1,5 4,5" fill="#FFDE00" />
        {/* Small stars */}
        <circle cx="10" cy="3" r="0.8" fill="#FFDE00" />
        <circle cx="12" cy="4.5" r="0.8" fill="#FFDE00" />
        <circle cx="12" cy="7" r="0.8" fill="#FFDE00" />
        <circle cx="10" cy="8.5" r="0.8" fill="#FFDE00" />
      </svg>
    );
  }

  if (code === 'GB') {
    return (
      <svg viewBox="0 0 24 16" className={className} aria-hidden="true">
        <rect width="24" height="16" fill="#012169" />
        {/* White diagonals */}
        <path d="M0,0 L24,16 M24,0 L0,16" stroke="#FFFFFF" strokeWidth="3" />
        {/* Red diagonals */}
        <path d="M0,0 L24,16 M24,0 L0,16" stroke="#C8102E" strokeWidth="1.5" />
        {/* White cross */}
        <rect x="10" width="4" height="16" fill="#FFFFFF" />
        <rect y="6" width="24" height="4" fill="#FFFFFF" />
        {/* Red cross */}
        <rect x="11" width="2" height="16" fill="#C8102E" />
        <rect y="7" width="24" height="2" fill="#C8102E" />
      </svg>
    );
  }

  if (code === 'JP') {
    return (
      <svg viewBox="0 0 24 16" className={className} aria-hidden="true">
        <rect width="24" height="16" fill="#FFFFFF" />
        <circle cx="12" cy="8" r="4.5" fill="#BC002D" />
      </svg>
    );
  }

  if (code === 'BR') {
    return (
      <svg viewBox="0 0 24 16" className={className} aria-hidden="true">
        <rect width="24" height="16" fill="#009739" />
        <polygon points="12,2 22,8 12,14 2,8" fill="#FEDD00" />
        <circle cx="12" cy="8" r="3" fill="#012169" />
      </svg>
    );
  }

  // Horizontal stripes (ES, DE, AR)
  if (flag.pattern === 'horizontal') {
    const stripeHeight = 16 / flag.colors.length;
    return (
      <svg viewBox="0 0 24 16" className={className} aria-hidden="true">
        {flag.colors.map((color, i) => (
          <rect key={i} y={i * stripeHeight} width="24" height={stripeHeight} fill={color} />
        ))}
      </svg>
    );
  }

  // Vertical stripes (FR)
  if (flag.pattern === 'vertical') {
    const stripeWidth = 24 / flag.colors.length;
    return (
      <svg viewBox="0 0 24 16" className={className} aria-hidden="true">
        {flag.colors.map((color, i) => (
          <rect key={i} x={i * stripeWidth} width={stripeWidth} height="16" fill={color} />
        ))}
      </svg>
    );
  }

  // Fallback
  return (
    <svg viewBox="0 0 24 16" className={className} aria-hidden="true">
      <rect width="24" height="16" fill="#CCCCCC" />
    </svg>
  );
}

/**
 * FlagIcon - Displays a country flag icon
 * Uses inline SVG for crisp rendering at any size
 */
export const FlagIcon = forwardRef<HTMLSpanElement, FlagIconProps>(
  ({ code, size = 'md', className, ...other }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center overflow-hidden rounded-sm',
          sizeClasses[size],
          className
        )}
        {...other}
      >
        <FlagSVG code={code} className="h-full w-full" />
      </span>
    );
  }
);

FlagIcon.displayName = 'FlagIcon';
